import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { CLIENTS } from "@/lib/data";
import { HOME_SECTIONS } from "@/constants/testIds";

const API = process.env.REACT_APP_BACKEND_URL;
const FALLBACK = CLIENTS.map((name, i) => ({ id: `static-${i}`, name, link_type: "none" }));

function LogoItem({ c }) {
  const inner = c.logo_url ? (
    <img
      src={c.logo_url.startsWith("/api/") ? `${API}${c.logo_url}` : c.logo_url}
      alt={c.alt || c.name}
      loading="lazy"
      className="h-8 w-auto max-w-[160px] object-contain opacity-60 grayscale transition-all duration-300 hover:opacity-100 hover:grayscale-0 lg:h-10"
    />
  ) : (
    <span className="font-display text-2xl italic tracking-tight text-ivory/50 transition-colors hover:text-gold lg:text-3xl">{c.name}</span>
  );
  const cls = "mx-10 inline-flex items-center";
  if (c.link_type === "external" && c.link_url) {
    return (
      <a href={c.link_url} target={c.open_in === "new" ? "_blank" : undefined} rel={c.open_in === "new" ? "noopener noreferrer" : undefined} className={cls} data-testid={`client-logo-${c.id}`} title={c.description || c.name}>
        {inner}
      </a>
    );
  }
  if (c.link_type === "internal" && c.link_url) {
    return (
      <Link to={c.link_url} target={c.open_in === "new" ? "_blank" : undefined} className={cls} data-testid={`client-logo-${c.id}`} title={c.description || c.name}>
        {inner}
      </Link>
    );
  }
  return <span className={cls} data-testid={`client-logo-${c.id}`}>{inner}</span>;
}

export default function ClientStrip() {
  const [clients, setClients] = useState(FALLBACK);

  useEffect(() => {
    fetch(`${API}/api/public/client-logos`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => { if (Array.isArray(data) && data.length) setClients(data); })
      .catch(() => {});
  }, []);

  const row = [...clients, ...clients];
  return (
    <section data-testid={HOME_SECTIONS.clients} className="relative border-y border-ivory/[0.06] bg-ink-950 py-10">
      <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
        <div className="flex items-center gap-6">
          <span className="hidden shrink-0 font-sans text-[11px] uppercase tracking-widest text-gold lg:inline">In good company</span>
          <div className="relative flex-1 overflow-hidden">
            <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-16 bg-gradient-to-r from-ink-950 to-transparent" />
            <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-16 bg-gradient-to-l from-ink-950 to-transparent" />
            <motion.div
              className="flex items-center whitespace-nowrap"
              animate={{ x: ["0%", "-50%"] }}
              transition={{ duration: Math.max(30, clients.length * 2.2), ease: "linear", repeat: Infinity }}
            >
              {row.map((c, i) => <LogoItem key={`${c.id}-${i}`} c={c} />)}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
